<template>
    <div>
        <pc-label
            v-if="label"
            v-bind="labelBinder"
            v-on:help-clicked="$emit('help-clicked')" />
        <div class="d-block d-print-none">
            <!--
                Must allow click to propagate from inner button to this wrapper,
                but stop it from escaping past this in case this control is contained
                within something else with a click handler
            -->
            <div
                class="input-group"
                v-on:click.stop="showPickerModal">
                <input
                    v-bind="binder"
                    ref="input"
                    v-model="internalValue"
                    v-bind:required="required"
                    v-bind:aria-invalid="hasValidationError"
                    v-bind:aria-describedby="validationErrorId"
                    v-bind:disabled="shouldDisable"
                    class="form-control"
                    no-interact
                    v-on="listeners" />
                <!--
                    Must allow click event to bubble up for this to work in both
                    wide and narrow mode
                -->
                <button
                    v-bind:class="{
                        'invalid-icon': hasValidationError,
                        'btn-secondary': !showCalendarButtonAsDisabled
                    }"
                    v-bind:disabled="showCalendarButtonAsDisabled"
                    class="btn border btn-secondary input-group-text"
                    type="button"
                    data-test-role="date-picker-btn"
                    v-on:click="showPicker">
                    <span class="visually-hidden">Calendar</span>
                    <pc-bs-icon name="calendar3" />
                </button>
            </div>
            <div
                v-if="showValidationErrors"
                v-bind:id="validationErrorId"
                v-bind:class="{ 'd-block': hasValidationError }"
                class="invalid-feedback fw-bold">
                {{ validationError }}
            </div>
        </div>
        <div class="d-none d-print-block">
            <div class="form-control">
                {{ internalValue }}
            </div>
        </div>
        <pc-modal
            ref="modal"
            v-bind:show-header="false">
            <div
                ref="inlineCal"
                class="pc-date-modal" />
            <template v-slot:footer>
                <pc-button
                    primary
                    v-on:click="clearDate">
                    Clear
                </pc-button>
            </template>
        </pc-modal>
    </div>
</template>

<script>
    import "jquery-ui/ui/widgets/datepicker";
    import base from "./_inputBase.vue";
    import $ from "jquery";
    import { responsiveManager } from "Responsive/manager";

    export default {
        extends: base,
        emits: ["help-clicked"],
        data: function () {
            const dateOptions = {
                showOtherMonths: true,
                selectOtherMonths: true,
                dateFormat: "m/d/yy",
                nextText: "",
                prevText: "",
                dayNamesMin: ["S", "M", "T", "W", "Th", "F", "S"],
                onSelect: (dateText) => {
                    this.internalValue = dateText;
                    this.$refs.modal.hide();
                }
            };
            return {
                dateOptions,
                internalType: "text"
            };
        },
        computed: {
            showCalendarButtonAsDisabled() {
                return this.readonly || this.disabled;
            },
            shouldDisable() {
                return this.readonly || this.disabled || !responsiveManager.md.value;
            },
            showAsNotDisabled() {
                return !this.readonly && !this.disabled && !responsiveManager.md.value;
            },
            overrideInputClasses() {
                return { "bg-white": this.showAsNotDisabled };
            }
        },
        mounted: function () {
            // use Program's locale to set prarameters for date picker.  Override U.S. specific defaults set above.
            const dateOptionsOverride = {
                ...this.dateOptions,
                dayNamesMin: this.$app.DateCultureMessageInfo.AbbreviatedDaysOfTheWeek,
                dayNames: this.$app.DateCultureMessageInfo.DaysOfTheWeek,
                monthNames: this.$app.DateCultureMessageInfo.MonthsOfTheYear,
                dateFormat: this.$app.DateCultureMessageInfo.ShortDateFormat
            };
            $(this.$refs.input).datepicker(dateOptionsOverride);

            const $element = $(this.$refs.inlineCal);
            $element.datepicker(dateOptionsOverride);
            $element.datepicker("setDate", this.internalValue);
        },
        unmounted: function () {
            $(this.$refs.input).datepicker("destroy");
            $(this.$refs.inlineCal).datepicker("destroy");
        },
        methods: {
            afterEmitValue(value) {
                $(this.$refs.inlineCal).datepicker("setDate", value);
            },
            clearDate() {
                this.internalValue = "";
                this.$refs.modal.hide();
            },
            showPicker() {
                if (this.readonly || this.disabled) {
                    return;
                }
                if (responsiveManager.md.value) {
                    $(this.$refs.input).datepicker("show");
                }
            },
            showPickerModal() {
                if (this.readonly || this.disabled) {
                    return;
                }
                if (!responsiveManager.md.value) {
                    this.$refs.modal.show();
                }
            }
        }
    };
</script>

<style lang="scss">
    #ui-datepicker-div {
        display: none;
        z-index: 10000 !important;
    }
    .invalid-icon {
        border-color: var(--bs-form-invalid-border-color);
    }
    .pc-date-modal .ui-datepicker {
        border: none;
        box-shadow: none;
        padding: 0;
    }
    .pc-date-modal .ui-datepicker-calendar {
        width: 100%;
    }
    .ui-datepicker {
        border: 1px solid var(--bs-gray-500);
        background-color: white;
        box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.4);
        z-index: 10000;
        margin-top: -1px;
        padding: 8px;
    }
    .ui-datepicker .ui-datepicker-title {
        text-align: center;
    }
    .ui-datepicker .ui-datepicker-next {
        display: block;
        float: right;
        width: 14px;
        height: 14px;
        cursor: pointer;
        background-image: url("bootstrap-icons/icons/chevron-right.svg");
        background-position: -432px -112px;
    }
    .ui-datepicker .ui-datepicker-prev {
        display: block;
        float: left;
        width: 14px;
        height: 14px;
        cursor: pointer;
        background-image: url("bootstrap-icons/icons/chevron-left.svg");
        background-position: -432px -112px;
    }
    .ui-datepicker .ui-datepicker-calendar tr td {
        padding: 4px;
    }
    .ui-datepicker .ui-datepicker-calendar tr td a {
        display: block;
        width: 20px;
    }
    .ui-datepicker .ui-datepicker-calendar tr th {
        padding: 4px;
    }
    .ui-datepicker .ui-datepicker-today {
        background-color: var(--bs-gray-200);
    }
    .ui-datepicker .ui-datepicker-current-day {
        background-color: var(--bs-primary);
    }
    .ui-datepicker .ui-datepicker-current-day a {
        color: white;
    }
    .ui-datepicker .ui-datepicker-other-month a {
        color: var(--bs-gray-500);
    }
</style>
